import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120pcSchEService } from './f1120pc-sch-e.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120pc-sch-e',
    templateUrl: './f1120pc-sch-e.component.html',
    styleUrls: ['./f1120pc-sch-e.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120pcSchEComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS
  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Component properties migrated from FormScheduleECtrl.js
  modalTitle = "Schedule E Premiums Earned—Section 832 (see instructions)";
  premiumsEarned = 0;
  unallocatedAmount = 0;
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  leId: string = '';
  formName = '1120PC';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  systemFlag = 'D';
  showFormLoader = true;
  crudLoading = false;
  
  // Flag to prevent multiple initialization
  private isInitialized = false;

  // Form data structure
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Entity and form data
  entityList: any[] = [];
  scheduleEFormView: any = { rows: [] };
  formListOriginal: any = {};
  unallocatedAmountData: any[] = [];
  unallocatedAmountDataOriginal: any[] = []; // Backup of original unallocated amount data for reset
  BatchList: any[] = [];
  BatchListData: any[] = [];

  // Tab configuration
  tabs = [
    { id: 1, name: "Form Entry", active: true },
    { id: 2, name: "Form View", active: false },
    { id: 3, name: "Audit", active: false }
  ];
  activeTab = 1;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120pcSchEService: F1120pcSchEService,
    private _snackBar: MatSnackBar,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      if (this.isInitialized) {
        return;
      }

      if (this.rowData && Object.keys(this.rowData).length > 0) {
        console.log('Initializing F1120PC-SCH-E component');
        this.isInitialized = true;
        this.setupEntityData();
        this.initScheduleE();
      }
    } catch (e) {
      console.error('Error in F1120PC-SCH-E init:', e);
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;
     
    // ENTITY ORDER: Tax Year, LEID, LE Name, LE TYPE, ME (5 columns as requested)
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME},
      {key: "LE TYPE", value: this.rowData.LE_TYPE},
      {key: "ME", value: this.rowData.ME}
    ];

    this.unallocatedAmountData = this.rowData.dig || [];
    // Create backup of original unallocated amount data for reset
    this.unallocatedAmountDataOriginal = _.cloneDeep(this.unallocatedAmountData);
  }

  initScheduleE() {
    // Load main data first, then parallel load supporting data
    this.fetchSchErecords();
    this.fetchSchEimports();
  }

  fetchSchErecords() {
    const params = { 
      "action_id": 603, 
      "tax_year": this.rowData.TAX_YEAR, 
      "scenario": this.rowData.SCENARIO, 
      "jcd_key": this.rowData.JCD_KEY, 
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  fetchSchEimports() {
    const params = { 
      "client_key": this.appService.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch E", // Use filterParams or fallback
      "status": 'Y'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Batch data response:', data); // Debug log
      this.BatchList = data.jsonObject || [];
      this.BatchList.forEach((value: any, key: number) => {
        this.BatchList[key].TOGGLE = false;
      });
      this.showRequestLoader = false;
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading batch data:', error);
      this.BatchList = [];
      this.showRequestLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    // CRITICAL: Reset the rows array before processing new data
    this.scheduleEFormView.rows = [];
    
    var dataRows = data.irsForm.formItemList;
    console.log('Processing form data, total rows:', dataRows.length);
    
    for(var i=1; i<dataRows.length; i++){
      var saveParamDtls: any[] = [];
      var tempDataObj: any = {};
      
      // CRITICAL: Set data BEFORE calling iterateArray
      tempDataObj.line_order = dataRows[i].dataRowList[0].rowDimensions.object_id.split('~')[8];
      tempDataObj.saveParams = dataRows[i].dataRowList[0].rowDimensions.object_id.split('~');
      
      // THEN call iterateArray (which uses saveParams)
      this.iterateArray(i, tempDataObj, dataRows[i]);
      
      console.log('Adding row:', tempDataObj.num, tempDataObj.description);
      this.scheduleEFormView.rows.push(tempDataObj);
    }
    
    console.log('Final scheduleEFormView.rows length:', this.scheduleEFormView.rows.length);
    this.formListOriginal = _.cloneDeep(this.scheduleEFormView);
    this.combinedComputeValues();
  }

  iterateArray(index: number, tempDataObj: any, dataRow: any) {
    for(var j=0; j<dataRow.dataRowList[0].dataCellList.length; j++){
      if (j==0){
        tempDataObj.num = dataRow.dataRowList[0].dataCellList[j].name;
      } else if (j==1){
        tempDataObj.description = dataRow.dataRowList[0].dataCellList[j].name;
      }else{
        tempDataObj.lineAttrKey = dataRow.dataRowList[0].dataCellList[j].key.split('~')[1];
        tempDataObj.attrib_name = dataRow.dataRowList[0].dataCellList[j].key.split('~')[0];
        tempDataObj.attrib_order = dataRow.dataRowList[0].dataCellList[j].key.split('~')[2];
        tempDataObj.is_editable = dataRow.dataRowList[0].dataCellList[j].property.is_editable_cell == 'Y' ? false : true;
        tempDataObj.is_display = dataRow.dataRowList[0].dataCellList[j].property.display == 'Y' ? true : false;
        const rawValue = dataRow.dataRowList[0].dataCellList[j].value;
        tempDataObj.value = rawValue == null ? 0 : Number(rawValue);
        
        // Debug log to see what values we're getting from API
        if (j === 2) { // Only log for the value column
          console.log(`Row ${tempDataObj.num}: raw="${rawValue}", parsed=${tempDataObj.value}, type=${typeof tempDataObj.value}`);
        }
        
        // SAFE: Check if saveParams exists and has required length
        tempDataObj.is_form_display = tempDataObj.saveParams && tempDataObj.saveParams.length > 9  
          ? tempDataObj.saveParams[9]  
          : '';
      }
    }
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const params = { 
      "client_key": this.appService.USER_SETTINGS.user.client_key,
      "process_name": this.filterParams.process_name || "1120PC Sch E", // Use filterParams or fallback
      "transaction_key": trans_key
    };
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('Transaction data response:', data); // Debug log
      this.batchLoader = false;
      this.showBatchData = true;
      this.BatchListData = data.jsonObject || [];
      this.cdr.markForCheck();
    }, (error: any) => {
      console.error('Error loading transaction data:', error);
      this.batchLoader = false;
      this.showBatchData = false;
      this.BatchListData = [];
      this.cdr.markForCheck();
    });
  }

  private prev_index: number | null = null;

  showUploads(index: number, trans_key: string) {
    console.log('showUploads called:', {index, trans_key, prev_index: this.prev_index});
    
    if(this.prev_index !== null){
      if(this.prev_index !== index){
        console.log('Switching to different item');
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      }else{
        console.log('Toggling same item');
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        // If toggling off, hide the data; if toggling on, show it (assuming data already loaded)
        this.showBatchData = this.BatchList[index].TOGGLE;
        console.log('Toggle state:', this.BatchList[index].TOGGLE, 'showBatchData:', this.showBatchData);
      }
    }else{
      console.log('First time click');
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  combinedComputeValues(row?: any) {
    switch (this.rowData.TAX_YEAR) {
      case 2017:
        this.computeValues();
        break ;
      case 2018:
        this.computeValues();
        break;
      case 2019:
        this.computeValues();
        break;
      default:
        this.computeValues();
    }
  }

  computeValues() {
    // Check if we have enough rows before computing
    if (!this.scheduleEFormView.rows || this.scheduleEFormView.rows.length < 17) {
      return;
    }

    console.log('computeValues called, raw values:', {
      line1: this.scheduleEFormView.rows[0].value,
      line2a: this.scheduleEFormView.rows[2].value,
      line2b: this.scheduleEFormView.rows[3].value,
      line2c: this.scheduleEFormView.rows[4].value,
      line2d: this.scheduleEFormView.rows[5].value,
      types: {
        line1: typeof this.scheduleEFormView.rows[0].value,
        line2a: typeof this.scheduleEFormView.rows[2].value,
        line2b: typeof this.scheduleEFormView.rows[3].value
      }
    });

    // Convert all values to numbers before calculation
    const line2a = Number(this.scheduleEFormView.rows[2].value) || 0;
    const line2b = Number(this.scheduleEFormView.rows[3].value) || 0;
    const line2c = Number(this.scheduleEFormView.rows[4].value) || 0;
    const line2d = Number(this.scheduleEFormView.rows[5].value) || 0;

    //Total. Add lines 2a through 2d (2e = 2a + 2b + 2c + 2d)
    this.scheduleEFormView.rows[6].value = line2a + line2b + line2c + line2d;
    
    console.log('2e calculation:', {
      line2a, line2b, line2c, line2d,
      result: this.scheduleEFormView.rows[6].value
    });

    // Convert to numbers and calculate line 3
    const line1 = Number(this.scheduleEFormView.rows[0].value) || 0;
    const line2e = Number(this.scheduleEFormView.rows[6].value) || 0;
    
    //Total. Add lines 1 and 2e (line 3 = line 1 + line 2e)
    this.scheduleEFormView.rows[7].value = line1 + line2e;
    
    console.log('Line 3 calculation:', {
      line1, line2e,
      result: this.scheduleEFormView.rows[7].value
    });

    // Convert to numbers for lines 4a-4d
    const line4a = Number(this.scheduleEFormView.rows[9].value) || 0;
    const line4b = Number(this.scheduleEFormView.rows[10].value) || 0;
    const line4c = Number(this.scheduleEFormView.rows[11].value) || 0;
    const line4d = Number(this.scheduleEFormView.rows[12].value) || 0;
    
    //Total. Add lines 4a through 4d (4e = 4a + 4b + 4c + 4d)
    this.scheduleEFormView.rows[13].value = line4a + line4b + line4c + line4d;

    // Convert to numbers for line 5 calculation
    const line3 = Number(this.scheduleEFormView.rows[7].value) || 0;
    const line4e = Number(this.scheduleEFormView.rows[13].value) || 0;
    
    //Subtract line 4e from line 3 (line 5 = line 3 - line 4e)
    this.scheduleEFormView.rows[14].value = line3 - line4e;

    // Convert to numbers for line 7 calculation
    const line5 = Number(this.scheduleEFormView.rows[14].value) || 0;
    const line6 = Number(this.scheduleEFormView.rows[15].value) || 0;
    
    //Premiums earned. Add lines 5 and 6. Enter here and on Schedule A, line 1 (line 7 = line 5 + line 6)
    this.scheduleEFormView.rows[16].value = line5 + line6;
    
    console.log('Final calculations:', {
      line5, line6,
      line7_result: this.scheduleEFormView.rows[16].value
    });

    this.premiumsEarned = this.scheduleEFormView.rows[16].value;

    // Update unallocated amount data
    if (this.unallocatedAmountData && this.unallocatedAmountData.length > 0) {
      this.unallocatedAmountData[0][0].value = this.unallocatedAmountData[1][0].value = this.scheduleEFormView.rows[16].value;
      this.unallocatedAmountData[0][2].value = (this.unallocatedAmountData[0][0].value || 0) - (this.unallocatedAmountData[0][1].value || 0);
      this.unallocatedAmountData[1][2].value = (this.unallocatedAmountData[1][0].value || 0) - (this.unallocatedAmountData[1][1].value || 0);
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    var formRowData = {};
    var tempArray = [];

    for(var i=0; i<this.scheduleEFormView.rows.length ;i++){
      formRowData = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: this.scheduleEFormView.rows[i].saveParams[1],
        line_order: this.scheduleEFormView.rows[i].line_order,
        attrib_order: this.scheduleEFormView.rows[i].attrib_order,
        line_description: this.scheduleEFormView.rows[i].description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: this.scheduleEFormView.rows[i].saveParams[2],
        line_no: this.scheduleEFormView.rows[i].num,
        occurence: this.scheduleEFormView.rows[i].saveParams[7],
        line_attrib_key: this.scheduleEFormView.rows[i].lineAttrKey,
        Attribute_Name: this.scheduleEFormView.rows[i].attrib_name,
        Attribute_Value: this.scheduleEFormView.rows[i].value,
        trans_dtls_key: this.scheduleEFormView.rows[i].saveParams[3],
        trans_status: this.scheduleEFormView.rows[i].saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowData);
    }
    this.formData.editrows.push(tempArray);

    const mainData = [];
    mainData.push(this.formData);

    // Build diagnostic data
    const diagData: any[] = [];
    if (this.unallocatedAmountData) {
      this.unallocatedAmountData.forEach((array: any[], key: number) => {
        array.forEach((obj: any, key: number) => {
          if(key === 3){
            diagData.push(obj);
          }
        });
      });
    }

    this.f1120pcSchEService.saveF1120pcSchEForm(
      this.GENERAL_CONFIG.base_url, 
      mainData, 
      diagData,
      this.globalService, 
      this.filterParams, 
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {  
            duration: 4000, 
            panelClass: ['success-snackbar'] 
          });
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {  
            duration: 4000, 
            panelClass: ['error-snackbar'] 
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {  
          duration: 4000, 
          panelClass: ['error-snackbar'] 
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    // Reset main form
    this.scheduleEFormView = _.cloneDeep(this.formListOriginal);
    
    // Reset inputs in the last column of the last table (unallocatedAmountData)
    // Restore from original backup to preserve initial server values
    this.unallocatedAmountData = _.cloneDeep(this.unallocatedAmountDataOriginal);
    
    // Recalculate computed values
    this.combinedComputeValues();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
    // NO $uibModalInstance.dismiss('cancel') or this.dialog.closeAll()
  }

  // Update value method to ensure proper number conversion
  updateValue(row: any, event: any) {
    const inputElement = event.target as HTMLInputElement;
    const rawValue = inputElement.value;
    const numValue = Number(rawValue) || 0;
    
    console.log(`UpdateValue for ${row.num}: raw="${rawValue}", parsed=${numValue}`);
    
    row.value = numValue;
    this.computeValues();
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.lineAttrKey || item.num || index : index;
  }
}
